"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import {
  BackendButton,
  backendSelectTriggerClass,
} from "@/app/components/backend/backend-ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/ui/popover";
import ValidationPopover, {
  type ValidationPopoverBgT,
  type ValidationErrorT,
} from "@/app/components/ui/validation-popover";
import { useMultiSelectLogic } from "@/app/hooks/multiselect/use-multi-select-logic";
import { useVisibleCount } from "@/app/hooks/multiselect/use-visible-count";
import { useMultiSelectFiltering } from "@/app/hooks/multiselect/use-multi-select-filtering";
import { SelectionBadges } from "../../multi-select/selection-badges";
import { TriggerControls } from "../../multi-select/trigger-controls";
import { DropdownContent } from "../../multi-select/dropdown-content";
import { BackendLabel } from "../../backend-label";
import type { MultiSelectOptionT } from "@/app/components/ui/multi-select";

/**
 * Props for the BackendMultiSelect component
 *
 * Extends the base MultiSelect functionality with backend-specific styling
 * that matches the BackendInput component design.
 */
export interface BackendMultiSelectProps {
  /** Array of available options to choose from */
  options: MultiSelectOptionT[];
  /** Currently selected option values */
  selectedValues: string[];
  /** Callback function called when selection changes */
  onSelectionChange: (values: string[]) => void;
  /** When true and maxSelections=1, returns single string instead of array */
  returnSingleValue?: boolean;
  /** Placeholder text shown when no options are selected */
  placeholder?: string;
  /** Placeholder text for the search input field */
  searchPlaceholder?: string;
  /** Message shown when no options match the search */
  emptyMessage?: string;
  /** Label displayed above the input field */
  label?: string;
  /** Whether this field is required (shows red asterisk) */
  required?: boolean;
  /** Whether the entire component is disabled */
  disabled?: boolean;
  /** Whether the component is read-only (allows opening dropdown but prevents selection) */
  readOnly?: boolean;
  /** Error state - can be boolean or error message string */
  error?: boolean | string;
  /** Additional CSS classes for styling */
  className?: string;
  /** Maximum number of selections */
  maxSelections?: number;
  /** Callback function called when the component loses focus */
  onBlur?: () => void;
  /** Callback function called when the component is clicked */
  onClick?: () => void;
  /** Callback function called when the component is touched (mobile) */
  onTouchStart?: () => void;
  /** Automatically close dropdown after selecting an option */
  closeOnSelect?: boolean;
  /** Disable popover portal rendering (useful in dialogs to ensure scroll/interaction works) */
  disablePortal?: boolean;
  /** Validation messages shown in the popover */
  validationMessages?: (string | ValidationErrorT)[];
  /** Background variant for the validation popover */
  validationBgVariant?: ValidationPopoverBgT;
  /** Vertical offset for the validation popover */
  validationSideOffset?: number;
}

/**
 * BackendMultiSelect - Clean component architecture with extracted sub-components
 *
 * Separation of concerns:
 * - Business logic via custom hooks (useMultiSelectLogic, useVisibleCount, useMultiSelectFiltering)
 * - UI components: SelectionBadges, TriggerControls, DropdownContent
 * - Main component focuses on orchestration and styling
 *
 * A backend-specific multi-select dropdown with consistent styling
 * that matches the BackendInput component design patterns.
 */
export const BackendMultiSelect = React.forwardRef<
  HTMLButtonElement,
  BackendMultiSelectProps
>(function BackendMultiSelect(
  {
    options,
    selectedValues,
    onSelectionChange,
    returnSingleValue = false,
    placeholder = "Optionen auswählen...",
    searchPlaceholder = "Suchen...",
    emptyMessage = "Keine Optionen gefunden.",
    label,
    required = false,
    disabled = false,
    readOnly = false,
    error = false,
    className,
    maxSelections,
    onBlur,
    onClick,
    onTouchStart,
    closeOnSelect = false,
    disablePortal = false,
    validationMessages: externalValidationMessages,
    validationBgVariant = "accent",
    validationSideOffset = 0,
  }: BackendMultiSelectProps,
  triggerRef
) {
  const [open, setOpen] = React.useState(false);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        onBlur?.();
      }
    },
    [onBlur]
  );

  // Enhanced selection change handler for single value mode
  const enhancedOnSelectionChange = React.useCallback(
    (values: string[]) => {
      if (returnSingleValue && maxSelections === 1) {
        // For single value mode, convert array to single value or empty array
        onSelectionChange(values.length > 0 ? [values[0]] : []);
      } else {
        onSelectionChange(values);
      }
    },
    [onSelectionChange, returnSingleValue, maxSelections]
  );

  // Custom hooks for clean separation of concerns
  const { selectedOptions, handleSelect, handleRemove, handleClearAll } =
    useMultiSelectLogic({
      options,
      selectedValues,
      onSelectionChange: enhancedOnSelectionChange,
      maxSelections,
      disabled: disabled || readOnly,
    });

  const { containerRef, visibleCount } = useVisibleCount({ selectedOptions });

  const { searchValue, setSearchValue, filteredOptions } =
    useMultiSelectFiltering({ options });

  // Determine if there's an error (either boolean true or non-empty string)
  const hasError = Boolean(error);

  const handleOptionSelect = React.useCallback(
    (value: string) => {
      // Prevent selection in read-only mode
      if (readOnly) return;
      handleSelect(value);

      if (closeOnSelect && !disabled && !readOnly) {
        handleOpenChange(false);
      }
    },
    [closeOnSelect, disabled, readOnly, handleOpenChange, handleSelect]
  );

  const inputStyles = cn(
    backendSelectTriggerClass,
    !readOnly && "!bg-[var(--c-card)]",
    readOnly && "!cursor-default !bg-[var(--c-surface-sunken)] select-text",
    hasError && "border-destructive focus:border-destructive",
    !className?.includes("h-") && "h-[42px]",
    "flex flex-nowrap items-center gap-2",
    className
  );

  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  const mergedTriggerRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      buttonRef.current = node;
      if (typeof triggerRef === "function") {
        triggerRef(node);
      } else if (triggerRef) {
        (
          triggerRef as React.MutableRefObject<HTMLButtonElement | null>
        ).current = node;
      }
    },
    [triggerRef]
  );

  const validationMessages = React.useMemo(() => {
    if (externalValidationMessages && externalValidationMessages.length > 0) {
      return externalValidationMessages;
    }

    if (typeof error === "string" && error.trim().length > 0) {
      return [error];
    }

    return [] as (string | ValidationErrorT)[];
  }, [externalValidationMessages, error]);

  const shouldShowValidation = validationMessages.length > 0;

  return (
    <div className="flex w-full flex-col gap-1 sm:gap-1.5">
      {/* Label with optional required indicator */}
      {label && <BackendLabel required={required}>{label}</BackendLabel>}

      <div className="relative text-sm">
        <ValidationPopover
          error={shouldShowValidation ? validationMessages : undefined}
          triggerRef={buttonRef}
          triggerOnHover={shouldShowValidation}
          bgVariant={validationBgVariant}
          sideOffset={validationSideOffset}
        >
          <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <BackendButton
                variant="outline"
                role="combobox"
                aria-expanded={open}
                aria-invalid={hasError || undefined}
                className={cn(
                  inputStyles,
                  "justify-between font-normal",
                  !selectedValues.length &&
                    "text-sm font-normal text-[var(--c-muted)]",
                  readOnly ? "cursor-default" : "!cursor-pointer"
                )}
                disabled={disabled}
                onClick={onClick}
                onTouchStart={onTouchStart}
                ref={mergedTriggerRef}
              >
                {/* Selected badges container with placeholder and overflow handling */}
                <SelectionBadges
                  selectedOptions={selectedOptions}
                  visibleCount={visibleCount}
                  placeholder={placeholder}
                  disabled={disabled || readOnly}
                  onRemove={handleRemove}
                  containerRef={containerRef}
                />

                {/* Clear all button and chevron icon */}
                <TriggerControls
                  hasSelections={selectedValues.length > 0}
                  disabled={disabled || readOnly}
                  onClearAll={handleClearAll}
                />
              </BackendButton>
            </PopoverTrigger>

            {/* Dropdown content with search and selectable options */}
            <PopoverContent
              className="w-[var(--radix-popover-trigger-width)] min-w-[220px] !animate-none overflow-hidden rounded-[14px] border-0 bg-[var(--c-card)] p-0 text-[var(--c-text)] shadow-[0_16px_44px_rgba(80,60,20,0.18)]"
              align="start"
              portalled={!disablePortal}
            >
              <DropdownContent
                filteredOptions={filteredOptions}
                selectedValues={selectedValues}
                searchValue={searchValue}
                onSearchChange={readOnly ? undefined : setSearchValue}
                onSelect={handleOptionSelect}
                searchPlaceholder={searchPlaceholder}
                emptyMessage={emptyMessage}
                maxSelections={maxSelections}
                readOnly={readOnly}
              />
            </PopoverContent>
          </Popover>
        </ValidationPopover>
      </div>
    </div>
  );
});

BackendMultiSelect.displayName = "BackendMultiSelect";
